import { getLocaleString } from '@assets/i18n/config';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { formatLastRefreshed } from '@utils/formatLastRefreshed';
import { getMarketStatus } from '@utils/marketStatus';
import { formatMoneyValue } from '@utils/moneyFormatting';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

export type SnapshotMetadataHeaderProps = {
  /** The date to display. Defaults to `new Date()` when omitted. */
  date?: Date;
  portfolioCount: number;
  /** Total AUM expressed in cents. */
  aumCents: number;
  /** BCP-47 locale used for date formatting. Defaults to the user's active locale via `getLocaleString()`. */
  locale?: string;
  /** Optional handler invoked when the refresh icon is clicked. Renders the button only when provided. */
  onRefresh?: () => void;
};

/**
 * SnapshotMetadataHeader — sits above the SnapshotCard grid: as-of date/time,
 * portfolio count, AUM, and a market-status (one subline, dot-separated)
 * pill (Pre-Market / Market Open / After Hours / Market Closed).
 *
 * Colours are derived from `useThemeTokens()`. The market status is computed
 * via the shared `getMarketStatus` util (also used by commandStrip) so both
 * surfaces stay in sync.
 */
export const SnapshotMetadataHeader = ({
  date,
  portfolioCount,
  aumCents,
  locale,
  onRefresh,
}: SnapshotMetadataHeaderProps): JSX.Element => {
  const { t } = useTranslation('snapshotCard');
  const effectiveLocale = locale ?? getLocaleString();
  const { sys } = useThemeTokens();
  const effectiveDate = date ?? new Date();
  const marketStatus = getMarketStatus(
    sys as unknown as { color: Record<string, string> },
    dayjs().toDate(),
  );

  const asOfLabel = formatLastRefreshed(effectiveDate, {
    locale: effectiveLocale,
  });

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant='headingSmall' sx={{ mb: 0.25 }}>
        {t('title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mt: 0.5,
        }}
      >
        <Typography
          variant='labelSmall'
          sx={{ color: sys.color.onSurfaceVariant }}
        >
          {t('asOfLabel', { value: asOfLabel })}
        </Typography>
        <Typography
          variant='labelSmall'
          sx={{ color: sys.color.outlineVariant }}
        >
          ·
        </Typography>
        <Typography
          variant='labelSmall'
          sx={{ color: sys.color.onSurfaceVariant }}
        >
          {t('portfolios', { count: portfolioCount })}
        </Typography>
        <Typography
          variant='labelSmall'
          sx={{ color: sys.color.outlineVariant }}
        >
          ·
        </Typography>
        <Typography
          variant='labelSmall'
          sx={{ color: sys.color.onSurfaceVariant }}
        >
          {t('aumLabel', { value: formatMoneyValue(aumCents) })}
        </Typography>
        <Typography
          variant='labelSmall'
          sx={{ color: sys.color.outlineVariant }}
        >
          ·
        </Typography>
        {/* Market status pill — mirrors commandStrip.tsx pill rendering */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: marketStatus.dot,
              animation:
                marketStatus.key === 'open' ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Typography
            variant='labelSmall'
            sx={{
              color: marketStatus.color,
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            {t(`marketStatus.${marketStatus.key}`)}
          </Typography>
        </Box>
        {onRefresh && (
          <IconButton size='small' onClick={onRefresh} label='Refresh'>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
